import React, { useState } from 'react';
import styles from './Contact.module.css';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from 'react-icons/fa';
import { addContactMessage } from '../../firebase/services';

function Contact({ lang }) {
  const t = {
    en: {
      getInTouch: 'Get in Touch',
      haveQuestions: "Have any questions? We'd love to hear from you.",
      yourName: 'Your Name',
      yourEmail: 'Your Email',
      subject: 'Subject',
      yourMessage: 'Your Message',
      send: 'Send Message',
      sending: 'Sending...',
      success: 'Thank you! Your message has been sent. We will get back to you soon.',
      error: 'Sorry, there was a problem sending your message. Please try again.',
      contactInfo: 'Contact Information',
      feelFree: 'Feel free to contact us through any of these channels',
      location: 'Our Location',
      phone: 'Phone Number',
      email: 'Email Address',
      workingHours: 'Working Hours',
      hours: 'Sun - Fri: 12:00 AM - 12:00 PM',
    },
    ar: {
      getInTouch: 'تواصل معنا',
      haveQuestions: 'هل لديك أي أسئلة؟ يسعدنا تواصلك معنا.',
      yourName: 'اسمك',
      yourEmail: 'بريدك الإلكتروني',
      subject: 'الموضوع',
      yourMessage: 'رسالتك',
      send: 'إرسال الرسالة',
      sending: 'جاري الإرسال...',
      success: 'شكرًا لك! تم إرسال رسالتك وسنرد عليك قريبًا.',
      error: 'عذرًا، حدثت مشكلة أثناء إرسال رسالتك. حاول مرة أخرى.',
      contactInfo: 'معلومات التواصل',
      feelFree: 'لا تتردد في التواصل معنا عبر أي من القنوات التالية',
      location: 'موقعنا',
      phone: 'رقم الهاتف',
      email: 'البريد الإلكتروني',
      workingHours: 'ساعات العمل',
      hours: 'الأحد - الجمعة: 12:00 صباحًا - 12:00 مساءً',
    },
    tr: {
      getInTouch: 'İletişime Geçin',
      haveQuestions: 'Herhangi bir sorunuz mu var? Bizimle iletişime geçmekten çekinmeyin.',
      yourName: 'Adınız',
      yourEmail: 'E-posta Adresiniz',
      subject: 'Konu',
      yourMessage: 'Mesajınız',
      send: 'Mesaj Gönder',
      sending: 'Gönderiliyor...',
      success: 'Teşekkürler! Mesajınız gönderildi. En kısa sürede size geri döneceğiz.',
      error: 'Üzgünüz, mesajınız gönderilirken bir sorun oluştu. Lütfen tekrar deneyin.',
      contactInfo: 'İletişim Bilgileri',
      feelFree: 'Aşağıdaki kanallardan herhangi biriyle bizimle iletişime geçebilirsiniz',
      location: 'Adresimiz',
      phone: 'Telefon Numarası',
      email: 'E-posta Adresi',
      workingHours: 'Çalışma Saatleri',
      hours: 'Pazar - Cuma: 00:00 - 12:00',
    }
  }[lang];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });
    
    try {
      const result = await addContactMessage(formData);
      
      if (result.success) {
        setSubmitMessage({ 
          type: 'success', 
          text: t.success 
        });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitMessage({ 
          type: 'error', 
          text: t.error 
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage({ 
        type: 'error', 
        text: t.error 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className={styles.contact}>
      <div className={styles.container}>
        <div className={styles.contactForm}>
          <h1>{t.getInTouch}</h1>
          <p>{t.haveQuestions}</p>
          
          <form onSubmit={handleSubmit} aria-label="Contact form">
            <div className={styles.formGroup}>
              <label htmlFor="name" className="sr-only">{t.yourName}</label>
              <input
                id="name"
                type="text"
                name="name"
                placeholder={t.yourName}
                value={formData.name}
                onChange={handleChange}
                required
                aria-required="true"
                aria-invalid={formData.name === ''}
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="email" className="sr-only">{t.yourEmail}</label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder={t.yourEmail}
                value={formData.email}
                onChange={handleChange}
                required
                aria-required="true"
                aria-invalid={formData.email === ''}
                aria-describedby="email-hint"
              />
              <span id="email-hint" className="sr-only">Please enter a valid email address</span>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="subject" className="sr-only">{t.subject}</label>
              <input
                id="subject"
                type="text"
                name="subject"
                placeholder={t.subject}
                value={formData.subject}
                onChange={handleChange}
                required
                aria-required="true"
                aria-invalid={formData.subject === ''}
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="message" className="sr-only">{t.yourMessage}</label>
              <textarea
                id="message"
                name="message"
                placeholder={t.yourMessage}
                value={formData.message}
                onChange={handleChange}
                required
                aria-required="true"
                aria-invalid={formData.message === ''}
                rows="6"
              ></textarea>
            </div>
            
            <button 
              type="submit" 
              className={styles.submitButton} 
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? t.sending : t.send}
            </button>

            {submitMessage.text && (
              <div 
                className={`${styles.submitMessage} ${submitMessage.type === 'success' ? styles.success : styles.error}`}
                role="alert"
                aria-live="polite"
              >
                {submitMessage.text}
              </div>
            )}
          </form>
        </div>

        <div className={styles.contactInfo}>
          <h2>{t.contactInfo}</h2>
          <p>{t.feelFree}</p>
          
          <div className={styles.infoItems}>
            <div className={styles.infoItem}>
              <div className={styles.icon} aria-hidden="true">
                <FaMapMarkerAlt />
              </div>
              <div className={styles.details}>
                <h3>{t.location}</h3>
                <p>Turkey Istanbul</p>
              </div>
            </div>

            <div className={styles.infoItem}>
              <div className={styles.icon} aria-hidden="true">
                <FaPhone />
              </div>
              <div className={styles.details}>
                <h3>{t.phone}</h3>
                <p>
                  <a href="tel:+905386102862" aria-label="Call us at +905386102862">
                    +905386102862
                  </a>
                </p>
              </div>
            </div>

            <div className={styles.infoItem}>
              <div className={styles.icon} aria-hidden="true">
                <FaEnvelope />
              </div>
              <div className={styles.details}>
                <h3>{t.email}</h3>
                <p>
                  <a href="mailto:2nice.marka@gmail.com" aria-label="Email us at 2nice.marka@gmail.com">
                    2nice.marka@gmail.com
                  </a>
                </p>
              </div>
            </div>

            <div className={styles.infoItem}>
              <div className={styles.icon} aria-hidden="true">
                <FaClock />
              </div>
              <div className={styles.details}>
                <h3>{t.workingHours}</h3>
                <p>{t.hours}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;